import { CONSENT_MAX_AGE_DAYS, PRIVACY_POLICY_VERSION } from '@/data/privacyPolicy';

/**
 * בחירת העוגיות של המשתמש באזור האישי (HAT-1081). פורט של
 * LandingSite/LandingSite.client/src/lib/cookie-consent.ts עם תוספת אחת:
 * רשימת המחיקה כוללת גם את המפתחות של PostHog.
 *
 * מפתח ה-localStorage זהה לזה של אתר התדמית בכוונה: viewer-client (Angular, ב-/)
 * ו-new-viewer-client (React, ב-/new) חיים על אותו origin, כך שהסכמה שניתנה
 * באחד הלקוחות תקפה גם בשני. שינוי שם המפתח כאן מחייב שינוי זהה בלקוח השני.
 */
export interface CookieConsent {
  analytics: boolean;
  /** נוסח המדיניות שאושר - נוסח חדש יותר שואל את המשתמש מחדש. */
  policyVersion: string;
  /** מועד הבחירה (ISO), שממנו ההסכמה מתיישנת. */
  decidedAt: string;
}

const STORAGE_KEY = 'hatzdaka.cookie-consent';
const CONSENT_CHANGED_EVENT = 'hatzdaka:cookie-consent-changed';

const isStillValid = (decidedAt: string): boolean => {
  const age = Date.now() - new Date(decidedAt).getTime();
  return Number.isFinite(age) && age >= 0 && age < CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
};

/**
 * הבחירה התקפה של המשתמש, או null כשאין כזו - לא נבחר מעולם, נבחר מול נוסח ישן
 * של המדיניות, הבחירה בת יותר משנה, או שהדפדפן חוסם אחסון. כל אלה פירושם
 * "לשאול שוב", שהוא הכיוון הבטוח להיכשל אליו.
 */
export const readConsent = (): CookieConsent | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<CookieConsent>;
    if (typeof parsed?.analytics !== 'boolean') return null;
    if (parsed.policyVersion !== PRIVACY_POLICY_VERSION) return null;
    if (typeof parsed.decidedAt !== 'string' || !isStillValid(parsed.decidedAt)) return null;

    return { analytics: parsed.analytics, policyVersion: PRIVACY_POLICY_VERSION, decidedAt: parsed.decidedAt };
  } catch {
    return null;
  }
};

/** רושם את הבחירה ומודיע עליה לשאר העמוד - בלשונית הזו ובאחרות. */
export const writeConsent = (analytics: boolean): CookieConsent => {
  const consent: CookieConsent = {
    analytics,
    policyVersion: PRIVACY_POLICY_VERSION,
    decidedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // דפדפן שחוסם אחסון עדיין מקבל את הבחירה לעמוד הנוכחי; פשוט יישאל שוב בפעם הבאה.
  }

  window.dispatchEvent(new CustomEvent<CookieConsent>(CONSENT_CHANGED_EVENT, { detail: consent }));
  return consent;
};

/** מריץ את listener על כל שינוי מאוחר יותר של הבחירה, בלשונית הזו או באחרת. */
export const onConsentChanged = (listener: (consent: CookieConsent | null) => void): (() => void) => {
  const handleLocalChange = (event: Event) => listener((event as CustomEvent<CookieConsent>).detail);
  const handleOtherTabChange = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener(readConsent());
  };

  window.addEventListener(CONSENT_CHANGED_EVENT, handleLocalChange);
  window.addEventListener('storage', handleOtherTabChange);

  return () => {
    window.removeEventListener(CONSENT_CHANGED_EVENT, handleLocalChange);
    window.removeEventListener('storage', handleOtherTabChange);
  };
};

/**
 * מה שכלי המדידה ידועים כמשאירים על המכשיר, בהתאמת prefix כי כל אחד מהם מוסיף
 * מזהה לשם (‏_ga_G-XXXX‏, ph_phc_XXXX_posthog). המפתח של ההסכמה עצמה
 * (hatzdaka.cookie-consent) אינו נתפס באף prefix כאן - ואסור שייתפס.
 */
const MEASUREMENT_KEY_PREFIXES = [
  '_ga', // Google Analytics, including _gac_* conversion linking
  '_gid',
  '_gat',
  '__utm', // Google Analytics, older versions
  '_gcl', // Google Ads conversion linker
  '_hj', // Hotjar - לא בשימוש כאן, אבל יכול להיות מוזרק מבחוץ
  'ph_', // PostHog - העוגיות והמפתחות שהוא שומר ב-web storage
  'ai_user', // Azure Application Insights
  'ai_session',
];

const isMeasurementKey = (name: string) => MEASUREMENT_KEY_PREFIXES.some((prefix) => name.startsWith(prefix));

/** כל domain ו-path שעוגייה שלנו יכלה להיכתב עליהם - עוגייה נמחקת רק מה-scope המדויק שיצר אותה. */
const cookieScopes = () => {
  const { hostname, pathname } = window.location;

  const domains = hostname
    .split('.')
    .map((_, index, parts) => parts.slice(index).join('.'))
    .filter((domain) => domain.includes('.'));

  const segments = pathname.split('/').filter(Boolean);

  return {
    domains: [undefined, ...domains, ...domains.map((domain) => `.${domain}`)] as Array<string | undefined>,
    paths: ['/', ...segments.map((_, index) => `/${segments.slice(0, index + 1).join('/')}`)],
  };
};

const clearMeasurementCookies = () => {
  const { domains, paths } = cookieScopes();

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim();
    if (!name || !isMeasurementKey(name)) continue;

    for (const domain of domains) {
      for (const path of paths) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${path}${domain ? `; domain=${domain}` : ''}`;
      }
    }
  }
};

const clearMeasurementWebStorage = () => {
  // ה-accessors עצמם בתוך ה-try, לא רק הקריאות: דפדפן שחוסם עוגיות זורק כבר על
  // window.localStorage, וחריגה שבורחת מכאן מפילה את העמוד - זה רץ מ-effect וכל כמה שניות.
  for (const readStorage of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      const storage = readStorage();
      for (const key of Object.keys(storage)) {
        if (isMeasurementKey(key)) storage.removeItem(key);
      }
    } catch {
      // דפדפן שמסרב לאחסון - אין שם מה לנקות ממילא.
    }
  }
};

/**
 * מוחק כל מה שכלי המדידה עשויים לשמור על המכשיר - עוגיות וגם מפתחות web storage.
 * נקרא שוב ושוב כל עוד אין הסכמה, לא רק פעם אחת: כלי שהוזרק מבחוץ יכול להיטען
 * בכל רגע של הביקור ולשמור מזהה עד לניקוי הבא.
 */
export const clearMeasurementStorage = (): void => {
  clearMeasurementCookies();
  clearMeasurementWebStorage();
};
